import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, lazy, Suspense } from 'react';
import { Toaster } from 'sonner';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider, useCart } from './context/CartContext';
import { PreferenceProvider } from './context/PreferenceContext';
import { UpiCapabilitiesProvider } from './context/UpiCapabilitiesContext';
import { isTossitNativeApp, nativeLogout, setStatusBarStyle } from './utils/nativeBridge';
import { registerDeepLinkNavigator } from './utils/deepLink';
import useAnalytics from './hooks/useAnalytics';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import { initNativeAnalyticsBridge, ensureAppSessionStart } from './utils/analytics';
import { BottomNav } from './components/BottomNav';
import {
    HomePageSkeleton, AuthPageSkeleton, CartPageSkeleton, DownloadAppSkeleton,
    BookingsPageSkeleton, BookingDetailSkeleton, ProfilePageSkeleton, BuilderPageSkeleton,
    PaymentStatusSkeleton
} from './components/skeletons/CustomerSkeletons';
import { CookDashboardSkeleton, CookOrderDetailSkeleton } from './components/skeletons/CookSkeletons';
import { initMetaPixel, trackPageView } from './lib/metaPixel';
import '@/App.css';

// Customer app bundle
const AuthPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/AuthPage'));
const HomePage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/HomePage'));
const BuilderPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/BuilderPage'));
const HowItWorksPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/HowItWorksPage'));
const CartPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/CartPage'));
const MultiBookingPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/MultiBookingPage'));
const MultiBookingReviewPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/MultiBookingReviewPage'));
const MultiBookingCheckoutPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/MultiBookingCheckoutPage'));
const MultiBookingConfirmationPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/MultiBookingConfirmationPage'));
const OrderConfirmationPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/OrderConfirmationPage'));
const BookingsPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/BookingsPage'));
const BookingDetailPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/BookingDetailPage'));
const AddDishesReviewPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/AddDishesReviewPage'));
const ProfilePage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/ProfilePage'));
const FoodPreferencesSetupPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/FoodPreferencesSetupPage'));
const WalletPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/WalletPage'));
const WalletTopupCheckoutPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/WalletTopupCheckoutPage'));
const ReferralPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/ReferralPage'));
const PaymentStatusPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/PaymentStatusPage'));
const TermsPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/TermsPage'));
const PrivacyPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/PrivacyPage'));
const DeleteAccountPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/DeleteAccountPage'));
const DownloadAppPage = lazy(() => import(/* webpackChunkName: "customer" */ './pages/DownloadAppPage'));

// Admin app bundle
const AdminLoginPage = lazy(() => import(/* webpackChunkName: "admin" */ './pages/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import(/* webpackChunkName: "admin" */ './pages/AdminDashboardPage'));
const AdminChangePasswordPage = lazy(() => import(/* webpackChunkName: "admin" */ './pages/AdminChangePasswordPage'));
const AdminOrderDetailPage = lazy(() => import(/* webpackChunkName: "admin" */ './pages/AdminOrderDetailPage'));
const AdminCookDetailPage = lazy(() => import(/* webpackChunkName: "admin" */ './pages/AdminCookDetailPage'));

// Cook app bundle
const CookLoginPage = lazy(() => import(/* webpackChunkName: "cook" */ './pages/CookLoginPage'));
const CookDashboardPage = lazy(() => import(/* webpackChunkName: "cook" */ './pages/CookDashboardPage'));
const CookOrderDetailPage = lazy(() => import(/* webpackChunkName: "cook" */ './pages/CookOrderDetailPage'));
const CookIncentivesPage = lazy(() => import(/* webpackChunkName: "cook" */ './pages/CookIncentivesPage'));


const Spinner = () => (
    <div className="flex items-center justify-center min-h-screen bg-[#FAFAF9]">
        <div className="spinner" />
    </div>
);

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const native = isTossitNativeApp();

    // In the native WebView, notify the host to handle auth instead of showing
    // the web login page (fires on expiry / missing token).
    useEffect(() => {
        if (!loading && !user && native) {
            nativeLogout();
        }
    }, [loading, user, native]);

    if (loading) {
        return <Spinner />;
    }

    if (!user) {
        // Never route to the web login inside the app — host owns the login flow.
        if (native) {
            return <Spinner />;
        }
        return <Navigate to="/auth" replace />;
    }

    return children;
};

// Guards the /auth route. Inside the native WebView we never render the web login;
// we hand off to the host and keep the user inside the app (home is public).
const AuthGate = () => {
    const { user } = useAuth();
    const native = isTossitNativeApp();

    useEffect(() => {
        if (!user && native) {
            nativeLogout();
        }
    }, [user, native]);

    if (user) {
        const pending = sessionStorage.getItem('tossit_onboarding_redirect');
        if (pending) {
            sessionStorage.removeItem('tossit_onboarding_redirect');
            return <Navigate to={pending} replace />;
        }
        return <Navigate to="/" replace />;
    }
    if (native) return <Navigate to="/" replace />;
    return <AuthPage />;
};

const AppLayout = ({ children }) => {
    // Fetch + activate Remote Config on every customer app entry (skips admin/cook,
    // which never render inside AppLayout). Session-cached so it fetches once.
    useFeatureFlags();
    return (
        <div className="app-container bg-[#FAFAF9] min-h-screen">
            {children}
            <BottomNav />
        </div>
    );
};

// Serviceability resolver: for a customer with NO complete delivery address, resolve
// serviceability silently from current GPS (only if location permission is ALREADY
// granted — never prompts). Runs once per session. If it can't resolve silently, Home
// shows the lightweight "set your area" step. When a complete address exists, this is a no-op.
const NON_CUSTOMER_PREFIXES = ['/admin', '/cook', '/terms', '/privacy', '/delete-account', '/app', '/auth'];
const ServiceabilityResolver = () => {
    const { hasCompleteAddress, addressReady, locationStatus, runSilentServiceability } = useCart();
    const location = useLocation();
    const triedRef = useRef(false);

    const onCustomerRoute = !NON_CUSTOMER_PREFIXES.some(p => location.pathname.startsWith(p));

    useEffect(() => {
        if (!onCustomerRoute || !addressReady || hasCompleteAddress || triedRef.current) return;
        if (locationStatus.checked) return; // already resolved this session
        triedRef.current = true;
        runSilentServiceability();
    }, [onCustomerRoute, addressReady, hasCompleteAddress, locationStatus.checked, runSilentServiceability]);

    return null;
};

const AppRoutes = () => {
    const { loading } = useAuth();
    const location = useLocation();

    // Fire GA4 page_view on every SPA route change (mounted once, at the root).
    useAnalytics({ trackPageViews: true });

    // Register native→web analytics bridge callbacks + start the analytics session ONCE.
    useEffect(() => {
        initNativeAnalyticsBridge();
        ensureAppSessionStart();
    }, []);

    // Per-route app-shell fallback (auth init + lazy chunk download): each screen
    // gets a skeleton that mirrors ITS own layout. Any unknown customer path
    // (e.g. /webview or a native entry URL — all redirect to "/") defaults to the
    // home skeleton, NOT a spinner. Only admin/cook keep the neutral spinner.
    const p = location.pathname;
    const isAdminOrCook = p.startsWith('/admin') || p.startsWith('/cook');
    let shellFallback = isAdminOrCook ? <Spinner /> : <HomePageSkeleton />;
    if (p === '/cook/dashboard') shellFallback = <CookDashboardSkeleton />;
    else if (p.startsWith('/cook/orders/')) shellFallback = <CookOrderDetailSkeleton />;
    else if (p.startsWith('/auth')) shellFallback = <AuthPageSkeleton />;
    else if (p === '/cart' || p === '/checkout') shellFallback = <CartPageSkeleton />;
    else if (p.startsWith('/payment-status')) shellFallback = <PaymentStatusSkeleton />;
    else if (p.startsWith('/app')) shellFallback = <DownloadAppSkeleton />;
    else if (p === '/bookings') shellFallback = <BookingsPageSkeleton />;
    else if (p.startsWith('/bookings/')) shellFallback = <BookingDetailSkeleton />;
    else if (p.startsWith('/profile')) shellFallback = <ProfilePageSkeleton />;
    else if (p.startsWith('/builder')) shellFallback = <BuilderPageSkeleton />;

    // Swap the PWA manifest based on route so "Add to Home Screen" on /cook/*
    // installs the cook app (start_url /cook/login) instead of the user app.
    useEffect(() => {
        const isCook = location.pathname.startsWith('/cook');
        const link = document.querySelector('link[rel="manifest"]');
        if (link) {
            link.setAttribute('href', isCook ? '/cook-manifest.json' : '/manifest.json');
        }
        const titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
        if (titleMeta) {
            titleMeta.setAttribute('content', 'Tossit');
        }
    }, [location.pathname]);

    // Native status bar styling per route: home gets the dark-green header color
    // (light icons), every other page gets white (dark icons). Web/PWA theme-color too.
    useEffect(() => {
        const isHome = location.pathname === '/';
        setStatusBarStyle(isHome ? 'light' : 'dark', isHome ? '#277b3f' : '#FFFFFF');
        const tc = document.querySelector('meta[name="theme-color"]');
        if (tc) tc.setAttribute('content', isHome ? '#277b3f' : '#006f3c');
    }, [location.pathname]);

    // Meta Pixel — customer storefront only (never on /cook or /admin). Loads +
    // inits on the first customer route seen, then tracks PageView on each change.
    useEffect(() => {
        if (location.pathname.startsWith('/cook') || location.pathname.startsWith('/admin')) return;
        initMetaPixel();
        trackPageView();
    }, [location.pathname]);

    // Reset scroll to the top whenever the route changes.
    // iOS WKWebView is finnicky: momentum scrolling + late-mounting lazy routes mean a
    // single scrollTo fires too early or gets overridden. So we (a) disable the browser's
    // own scroll restoration and (b) re-apply the reset across several ticks / after paint.
    useEffect(() => {
        try {
            if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
        } catch { /* noop */ }
    }, []);

    useEffect(() => {
        // When navigating to an in-page anchor (e.g. /terms#cancellations-refunds), let the
        // destination page scroll to that section instead of forcing the top.
        if (location.hash) return;
        const toTop = () => {
            try { window.scrollTo(0, 0); } catch { /* noop */ }
            if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
            if (document.documentElement) document.documentElement.scrollTop = 0;
            if (document.body) document.body.scrollTop = 0;
        };
        toTop();
        const rafs = [];
        rafs.push(requestAnimationFrame(() => { toTop(); rafs.push(requestAnimationFrame(toTop)); }));
        // iOS WKWebView often needs a couple of later passes once the lazy route has painted
        const timers = [0, 60, 150, 300, 500].map(ms => setTimeout(toTop, ms));
        return () => {
            rafs.forEach(cancelAnimationFrame);
            timers.forEach(clearTimeout);
        };
    }, [location.pathname, location.hash]);

    if (loading) {
        return shellFallback;
    }

    const routes = (
        <Suspense fallback={shellFallback}>
        <Routes>
            {/* Public Routes - No auth required */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route path="/app" element={<DownloadAppPage />} />
            
            {/* Admin Routes - Outside of customer auth */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/dashboard/:tab" element={<AdminDashboardPage />} />
            <Route path="/admin/dashboard/:tab/:sub" element={<AdminDashboardPage />} />
            <Route path="/admin/order/:orderId" element={<AdminOrderDetailPage />} />
            <Route path="/admin/cook/:cookId" element={<AdminCookDetailPage />} />
            <Route path="/admin/change-password" element={<AdminChangePasswordPage />} />

            {/* Cook Routes - Outside of customer auth */}
            <Route path="/cook/login" element={<CookLoginPage />} />
            <Route path="/cook/dashboard" element={<CookDashboardPage />} />
            <Route path="/cook/orders/:orderId" element={<CookOrderDetailPage />} />
            <Route path="/cook/incentives" element={<CookIncentivesPage />} />

            {/* Auth Route */}
            <Route 
                path="/auth" 
                element={<AuthGate />} 
            />
            
            {/* Public Routes - Browse without login */}
            <Route
                path="/"
                element={
                    <AppLayout>
                        <HomePage />
                    </AppLayout>
                }
            />
            <Route
                path="/builder/:collectionId"
                element={<BuilderPage />}
            />
            <Route
                path="/cart"
                element={
                    <AppLayout>
                        <CartPage />
                    </AppLayout>
                }
            />
            <Route
                path="/multi-booking"
                element={
                    <AppLayout>
                        <MultiBookingPage />
                    </AppLayout>
                }
            />
            <Route
                path="/multi-booking/review"
                element={
                    <AppLayout>
                        <MultiBookingReviewPage />
                    </AppLayout>
                }
            />
            <Route
                path="/multi-booking/session/review"
                element={
                    <AppLayout>
                        <AddDishesReviewPage />
                    </AppLayout>
                }
            />
            <Route
                path="/multi-booking/checkout"
                element={
                    <AppLayout>
                        <MultiBookingCheckoutPage />
                    </AppLayout>
                }
            />
            <Route
                path="/multi-booking/confirmation"
                element={
                    <AppLayout>
                        <MultiBookingConfirmationPage />
                    </AppLayout>
                }
            />
            <Route
                path="/how-it-works"
                element={
                    <AppLayout>
                        <HowItWorksPage />
                    </AppLayout>
                }
            />
            <Route
                path="/checkout"
                element={<Navigate to="/cart" replace />}
            />
            
            {/* Protected Routes - Require login */}
            <Route
                path="/order-confirmation"
                element={
                    <ProtectedRoute>
                        <OrderConfirmationPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/payment-status"
                element={
                    <ProtectedRoute>
                        <PaymentStatusPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/bookings"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <BookingsPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/bookings/:id/add-dishes/review"
                element={
                    <ProtectedRoute>
                        <AddDishesReviewPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/bookings/:bookingId"
                element={
                    <ProtectedRoute>
                        <BookingDetailPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <ProfilePage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/preferences"
                element={
                    <ProtectedRoute>
                        <FoodPreferencesSetupPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/wallet"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <WalletPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/wallet/add"
                element={
                    <ProtectedRoute>
                        <WalletTopupCheckoutPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/refer"
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <ReferralPage />
                        </AppLayout>
                    </ProtectedRoute>
                }
            />
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
    );

    // Admin dashboard uses full desktop width; everything else (customer + cook
    // apps) renders in a centered mobile-first 480px column on a neutral backdrop.
    if (location.pathname.startsWith('/admin')) {
        return routes;
    }

    return (
        <div className="min-h-screen w-full bg-gray-100 flex justify-center">
            <div className="w-full max-w-[480px] min-h-screen bg-gray-50 relative" data-testid="mobile-frame">
                {routes}
                <ServiceabilityResolver />
            </div>
        </div>
    );
};

// Bridges native deep links to React Router. Mounted directly under BrowserRouter
// (not lazy) so it registers navigate() as early as possible; window.tossitNavigate
// itself is installed even earlier (index.js) and queues until this registers.
const DeepLinkBridge = () => {
    const navigate = useNavigate();
    useEffect(() => {
        registerDeepLinkNavigator((path) => navigate(path));
    }, [navigate]);
    return null;
};

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <DeepLinkBridge />
                <AuthProvider>
                    <CartProvider>
                        <PreferenceProvider>
                            <UpiCapabilitiesProvider>
                            <AppRoutes />
                            <Toaster 
                                position="top-center" 
                                richColors 
                                closeButton
                                toastOptions={{
                                    style: {
                                        fontFamily: 'Inter, sans-serif'
                                    }
                                }}
                            />
                            </UpiCapabilitiesProvider>
                        </PreferenceProvider>
                    </CartProvider>
                </AuthProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}

export default App;
